import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchEligibleSalesForReturn,
  fetchPreviousReturnedQuantitiesForSale,
  processReturnRpc,
  ProcessReturnPayload,
} from '@/services/returns';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Sale, SaleItem } from '@/types/models';
import {
  RotateCcw,
  Search,
  AlertTriangle,
  Package,
  Laptop as LaptopIcon,
  Wrench,
  Loader2,
  Info,
} from 'lucide-react';

interface SelectedReturnItem {
  sale_item_id: string;
  item_name: string;
  item_type: string;
  sku_or_serial: string | null;
  original_quantity: number;
  remaining_quantity: number;
  return_quantity: number;
  unit_refund: number;
  restocked: boolean;
  condition_on_return: string;
  notes: string;
}

interface CreateReturnFormProps {
  initialSale?: Sale;
  onSuccess: (returnId: string) => void;
  onCancel: () => void;
}

export const CreateReturnForm: React.FC<CreateReturnFormProps> = ({ initialSale, onSuccess, onCancel }) => {
  const { hasPermission } = useAuth();
  const isManagerOrOwner = hasPermission('purchases'); // owner & manager have 'purchases' permission
  const queryClient = useQueryClient();

  // Step state
  const [selectedSale, setSelectedSale] = useState<Sale | null>(initialSale ?? null);
  const [saleSearch, setSaleSearch] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Return Form State
  const [selectedItems, setSelectedItems] = useState<Record<string, SelectedReturnItem>>({});
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [customRefundAmount, setCustomRefundAmount] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Queries
  const { data: eligibleSales = [], isLoading: loadingSales, error: salesError, refetch: retrySales } = useQuery({
    queryKey: ['sales', 'eligible-for-return', saleSearch],
    queryFn: () => fetchEligibleSalesForReturn(saleSearch),
    enabled: isManagerOrOwner,
  });

  const { data: previousReturnedMap = {}, isLoading: loadingPrevious, error: previousError, refetch: retryPrevious } = useQuery<Record<string, number>>({
    queryKey: ['returns', 'previous-quantities', selectedSale?.id],
    queryFn: () => (selectedSale ? fetchPreviousReturnedQuantitiesForSale(selectedSale.id) : Promise.resolve<Record<string, number>>({})),
    enabled: !!selectedSale,
  });

  // Handle selecting a sale
  const handleSelectSale = (sale: Sale) => {
    setSelectedSale(sale);
    setSelectedItems({});
    setReason('');
    setNotes('');
    setCustomRefundAmount('');
    setSubmitError(null);
  };

  // Toggle item selection
  const handleToggleItem = (item: SaleItem, remaining: number) => {
    if (remaining <= 0 || loadingPrevious || previousError) return;

    setSelectedItems((prev) => {
      const next = { ...prev };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        const invoiceFactor = selectedSale && selectedSale.subtotal > 0 ? selectedSale.total / selectedSale.subtotal : 1;
        const defaultUnitRefund = item.quantity > 0 ? Math.round(((item.line_total / item.quantity) * invoiceFactor) * 100) / 100 : 0;
        next[item.id] = {
          sale_item_id: item.id,
          item_name: item.item_name,
          item_type: item.item_type,
          sku_or_serial: item.serial_snapshot || item.sku_snapshot || null,
          original_quantity: item.quantity,
          remaining_quantity: remaining,
          return_quantity: 1,
          unit_refund: defaultUnitRefund,
          restocked: true,
          condition_on_return: 'good',
          notes: '',
        };
      }
      return next;
    });
  };

  const updateSelectedItem = (saleItemId: string, updates: Partial<SelectedReturnItem>) => {
    setSelectedItems((prev) => {
      if (!prev[saleItemId]) return prev;
      return {
        ...prev,
        [saleItemId]: { ...prev[saleItemId], ...updates },
      };
    });
  };

  // Financial calculations
  const returnItemsList = Object.values(selectedItems);
  const totalReturnCalculatedValue = returnItemsList.reduce(
    (acc, item) => acc + item.return_quantity * item.unit_refund,
    0
  );

  const appliedToBalance = selectedSale
    ? Math.min(totalReturnCalculatedValue, Math.max(0, selectedSale.balance_due))
    : 0;

  const maximumCashRefund = Math.max(0, totalReturnCalculatedValue - appliedToBalance);

  const effectiveRefundAmount =
    customRefundAmount === ''
      ? maximumCashRefund
      : Math.max(0, Math.min(maximumCashRefund, Number(customRefundAmount)));

  const retainedDeduction = Math.max(0, maximumCashRefund - effectiveRefundAmount);

  // Mutation
  const returnMutation = useMutation({
    mutationFn: async () => {
      if (loadingPrevious || previousError) throw new Error('Load the previous returns successfully before processing a refund.');
      if (!selectedSale) throw new Error('No sale selected.');
      if (returnItemsList.length === 0) throw new Error('Please select at least one item to return.');
      if (!reason.trim()) throw new Error('Return reason is required.');

      const payload: ProcessReturnPayload = {
        sale_id: selectedSale.id,
        items: returnItemsList.map((item) => ({
          sale_item_id: item.sale_item_id,
          quantity: item.return_quantity,
          restocked: item.restocked,
          unit_refund: item.unit_refund,
          condition_on_return: item.condition_on_return || null,
          notes: item.notes || null,
        })),
        refund_amount: effectiveRefundAmount,
        refund_method: refundMethod,
        reason: reason.trim(),
        notes: notes.trim() || null,
      };

      return await processReturnRpc(payload);
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.returns.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      onSuccess(res.return_id);
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
      setShowConfirm(false);
    },
  });

  if (!isManagerOrOwner) {
    return (
      <div className="p-6 text-center space-y-3">
        <AlertTriangle className="w-10 h-10 text-brand-red mx-auto" />
        <h3 className="text-base font-bold text-brand-black">Manager / Owner Permission Required</h3>
        <p className="text-xs text-brand-muted max-w-sm mx-auto">
          Staff members are not permitted to process customer returns or issue refunds. Please ask an owner or manager to authorize this return.
        </p>
        <button onClick={onCancel} className="px-4 py-2 bg-brand-bg border text-xs font-semibold rounded">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1 text-xs">
      {submitError && <ErrorAlert message={submitError} />}
      {salesError && <ErrorAlert message={parseDatabaseError(salesError)} onRetry={() => { void retrySales(); }} />}
      {previousError && <ErrorAlert message="Previous returns could not be loaded. Retry before processing a refund." onRetry={() => { void retryPrevious(); }} />}

      {/* Step 1: Sale Picker */}
      {!selectedSale ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-brand-black uppercase tracking-wider">
              1. Select Invoice for Return
            </h4>
            <span className="text-[10px] text-brand-muted">Only Completed & Partially Refunded Sales</span>
          </div>

          <div className="relative">
            <input
              placeholder="Search invoice number or customer name..."
              value={saleSearch}
              onChange={(e) => setSaleSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-brand-bg border border-brand-border rounded text-xs"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
          </div>

          {loadingSales ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
            </div>
          ) : eligibleSales.length === 0 ? (
            <p className="p-8 text-center text-brand-muted italic border rounded bg-brand-bg">
              No eligible completed sales found matching your search.
            </p>
          ) : (
            <div className="max-h-60 overflow-y-auto divide-y divide-brand-border border rounded bg-white">
              {eligibleSales.map((sale) => (
                <div
                  key={sale.id}
                  onClick={() => handleSelectSale(sale)}
                  className="p-3 flex items-center justify-between hover:bg-brand-bg cursor-pointer transition-colors"
                >
                  <div>
                    <span className="font-bold text-brand-black mr-2">#{sale.invoice_number}</span>
                    <span className="text-brand-muted">
                      ({sale.customer?.name ?? 'Walk-in Customer'})
                    </span>
                    <p className="text-[10px] text-brand-muted mt-0.5">
                      Date: {new Date(sale.sale_date).toLocaleDateString('en-PK')} • Status: {sale.status}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-brand-green text-xs">
                      Rs. {sale.total.toLocaleString('en-PK')}
                    </span>
                    <p className="text-[10px] text-brand-muted">{sale.items?.length ?? 0} item(s)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Step 2: Configure Return Items & Reason */
        <div className="space-y-4">
          {/* Selected Sale Banner */}
          <div className="p-3 bg-brand-bg rounded-lg border border-brand-border flex items-center justify-between">
            <div>
              <span className="font-bold text-brand-black text-sm">Invoice #{selectedSale.invoice_number}</span>
              <p className="text-brand-muted">
                Customer: <strong className="text-brand-black">{selectedSale.customer?.name ?? 'Walk-in'}</strong> • Date: {new Date(selectedSale.sale_date).toLocaleDateString('en-PK')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedSale(null)}
              className="px-2.5 py-1 bg-white border text-xs font-semibold rounded hover:bg-brand-bg text-brand-muted"
            >
              Change Invoice
            </button>
          </div>

          {/* Items Selector */}
          <div className="space-y-2">
            <h4 className="font-bold text-brand-black uppercase tracking-wider flex items-center justify-between">
              <span>2. Select Sale Items to Return</span>
              <span className="text-[10px] text-brand-muted font-normal">Check items to include</span>
            </h4>

            {loadingPrevious ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 text-brand-green animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {selectedSale.items?.map((item) => {
                  const previouslyReturned = previousReturnedMap[item.id] || 0;
                  const remaining = Math.max(0, item.quantity - previouslyReturned);
                  const isSelected = !!selectedItems[item.id];
                  const selectedData = selectedItems[item.id];

                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-brand-green bg-green-50/40'
                          : remaining > 0
                          ? 'border-brand-border bg-white'
                          : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <label className="flex items-start space-x-2.5 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            disabled={remaining <= 0}
                            checked={isSelected}
                            onChange={() => handleToggleItem(item, remaining)}
                            className="mt-0.5 rounded text-brand-green focus:ring-brand-green"
                          />
                          <div>
                            <span className="font-semibold text-brand-black flex items-center space-x-1">
                              {item.item_type === 'laptop' && <LaptopIcon className="w-3.5 h-3.5 text-brand-green" />}
                              {item.item_type === 'accessory' && <Package className="w-3.5 h-3.5 text-blue-600" />}
                              {item.item_type === 'service' && <Wrench className="w-3.5 h-3.5 text-purple-600" />}
                              <span>{item.item_name}</span>
                            </span>
                            <p className="text-[10px] text-brand-muted">
                              Identifier: {item.serial_snapshot || item.sku_snapshot || 'N/A'} • Unit Price: Rs. {item.unit_price.toLocaleString('en-PK')}
                            </p>
                            <p className="text-[10px] text-brand-muted font-medium">
                              Original Qty: {item.quantity} | Previously Returned: {previouslyReturned} |{' '}
                              <strong className={remaining > 0 ? 'text-brand-green' : 'text-red-600'}>
                                Remaining Returnable: {remaining}
                              </strong>
                            </p>
                          </div>
                        </label>

                        <div className="text-right">
                          <span className="font-bold text-brand-black">Rs. {item.line_total.toLocaleString('en-PK')}</span>
                        </div>
                      </div>

                      {/* Expanded item return options when checked */}
                      {isSelected && selectedData && (
                        <div className="mt-3 pt-3 border-t border-brand-green/30 grid grid-cols-1 sm:grid-cols-4 gap-2 bg-white p-2.5 rounded border border-brand-green/20">
                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase">Return Qty</label>
                            <input
                              type="number"
                              min={1}
                              max={remaining}
                              value={selectedData.return_quantity}
                              onChange={(e) =>
                                updateSelectedItem(item.id, {
                                  return_quantity: Math.min(remaining, Math.max(1, Number(e.target.value) || 1)),
                                })
                              }
                              className="w-full px-2 py-1 bg-brand-bg border rounded font-bold"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase">Unit Refund (PKR)</label>
                            <input
                              type="number"
                              min={0}
                              value={selectedData.unit_refund}
                              onChange={(e) =>
                                updateSelectedItem(item.id, { unit_refund: Number(e.target.value) || 0 })
                              }
                              className="w-full px-2 py-1 bg-brand-bg border rounded font-semibold text-brand-green"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase">Restock to Inventory?</label>
                            <select
                              value={selectedData.restocked ? 'true' : 'false'}
                              onChange={(e) =>
                                updateSelectedItem(item.id, { restocked: e.target.value === 'true' })
                              }
                              className={`w-full px-2 py-1 border rounded font-bold ${
                                selectedData.restocked ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                              }`}
                            >
                              <option value="true">YES — Restock Stock</option>
                              <option value="false">NO — Damaged / Scrap</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase">Condition on Return</label>
                            <input
                              placeholder="e.g. good, faulty fan"
                              value={selectedData.condition_on_return}
                              onChange={(e) =>
                                updateSelectedItem(item.id, { condition_on_return: e.target.value })
                              }
                              className="w-full px-2 py-1 bg-brand-bg border rounded"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Overall Return Details */}
          {returnItemsList.length > 0 && (
            <div className="space-y-3 bg-brand-bg p-3 rounded-lg border border-brand-border">
              <h4 className="font-bold text-brand-black uppercase tracking-wider">
                3. Return Reason & Refund Method
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-brand-black uppercase mb-1">
                    Return Reason <span className="text-brand-red">*</span>
                  </label>
                  <input
                    placeholder="e.g. Defective battery, Customer changed mind..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-brand-border rounded font-medium"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-brand-black uppercase mb-1">
                    Refund Method
                  </label>
                  <select
                    value={refundMethod}
                    onChange={(e) => setRefundMethod(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-brand-border rounded font-semibold"
                  >
                    <option value="cash">Cash Refund</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_wallet">Mobile Wallet (JazzCash / EasyPaisa)</option>
                    <option value="card">Card Refund</option>
                    <option value="store_credit">Store Credit</option>
                  </select>
                </div>
              </div>

              {/* 5-Point Financial Breakdown Card */}
              <div className="p-3 bg-white rounded-lg border border-brand-border space-y-2">
                <div className="text-[11px] font-bold uppercase text-brand-muted tracking-wider">
                  Return Accounting Breakdown
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="p-2 bg-brand-bg rounded">
                    <span className="text-brand-muted block text-[10px]">Return Credit</span>
                    <strong className="text-brand-black">Rs. {totalReturnCalculatedValue.toLocaleString('en-PK')}</strong>
                  </div>
                  <div className="p-2 bg-brand-bg rounded">
                    <span className="text-brand-muted block text-[10px]">Applied to Balance</span>
                    <strong className="text-amber-700">Rs. {appliedToBalance.toLocaleString('en-PK')}</strong>
                  </div>
                  <div className="p-2 bg-brand-bg rounded">
                    <span className="text-brand-muted block text-[10px]">Max Cash Refund</span>
                    <strong className="text-brand-green">Rs. {maximumCashRefund.toLocaleString('en-PK')}</strong>
                  </div>
                  <div className="p-2 bg-brand-bg rounded">
                    <span className="text-brand-muted block text-[10px]">Retained Deduction</span>
                    <strong className={retainedDeduction > 0 ? 'text-brand-red' : 'text-brand-muted'}>
                      Rs. {retainedDeduction.toLocaleString('en-PK')}
                    </strong>
                  </div>
                </div>

                {appliedToBalance > 0 && maximumCashRefund === 0 && (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-900 font-medium">
                    Rs. {appliedToBalance.toLocaleString('en-PK')} of this return will be applied directly to the outstanding invoice balance. No cash refund is currently due.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-brand-black uppercase mb-1">
                    Cash Refund to Customer (PKR)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={maximumCashRefund}
                    placeholder={`Max Rs. ${maximumCashRefund.toLocaleString('en-PK')}`}
                    value={customRefundAmount}
                    onChange={(e) => setCustomRefundAmount(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-brand-border rounded font-bold text-brand-green"
                  />
                  <p className="text-[10px] text-brand-muted mt-0.5">
                    Entitled cash refund: <strong>Rs. {maximumCashRefund.toLocaleString('en-PK')}</strong>
                  </p>
                </div>

                <div>
                  <label className="block font-semibold text-brand-black uppercase mb-1">
                    Internal Notes {retainedDeduction > 0 && <span className="text-brand-red">*</span>}
                  </label>
                  <input
                    placeholder={retainedDeduction > 0 ? 'Deduction explanation required...' : 'Optional notes for audit log...'}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={`w-full px-3 py-1.5 bg-white border rounded ${
                      retainedDeduction > 0 && !notes.trim() && !reason.trim()
                        ? 'border-brand-red focus:ring-brand-red'
                        : 'border-brand-border'
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Summary Modal Step */}
          {showConfirm && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg space-y-3">
              <div className="flex items-center space-x-2 text-amber-900 font-bold text-sm">
                <Info className="w-5 h-5 text-amber-600 shrink-0" />
                <span>Confirm Return & Refund Submission</span>
              </div>
              <p className="text-amber-800">
                You are about to process a return of <strong>{returnItemsList.length} item(s)</strong> for Invoice <strong>#{selectedSale.invoice_number}</strong> with a total refund of{' '}
                <strong className="text-brand-black">Rs. {effectiveRefundAmount.toLocaleString('en-PK')}</strong> via <strong>{refundMethod}</strong>.
              </p>
              <div className="text-[11px] text-amber-900 bg-white p-2.5 rounded border border-amber-200 space-y-1">
                {returnItemsList.map((i) => (
                  <div key={i.sale_item_id} className="flex justify-between">
                    <span>
                      {i.item_name} (x{i.return_quantity})
                    </span>
                    <span className="font-bold">
                      {i.restocked ? '✓ Restock to inventory' : '✕ Damaged (No restock)'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={returnMutation.isPending || loadingPrevious || !!previousError}
                  className="px-3 py-1.5 bg-white border border-amber-300 text-amber-900 font-semibold rounded text-xs"
                >
                  Back to Edit
                </button>
                <button
                  type="button"
                  onClick={() => returnMutation.mutate()}
                  disabled={returnMutation.isPending || loadingPrevious || !!previousError}
                  className="inline-flex items-center space-x-1.5 px-4 py-1.5 bg-brand-green text-white font-bold rounded text-xs hover:bg-brand-green-hover"
                >
                  {returnMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  <span>{returnMutation.isPending ? 'Processing...' : 'Confirm & Process Return'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          {!showConfirm && (
            <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
              <button
                type="button"
                onClick={onCancel}
                disabled={returnMutation.isPending || loadingPrevious || !!previousError}
                className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={returnItemsList.length === 0 || !reason.trim() || loadingPrevious || !!previousError}
                onClick={() => setShowConfirm(true)}
                className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-sm"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Review & Process Return</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
